import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

export default function GlobalNav() {
  const [state, setState] = useState({ useOverride: false, hideBrand: false });
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // 监听视口宽度判断是否为移动端
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ====== IntersectionObserver 保持不变 ======
  useEffect(() => {
    const NAV_HEIGHT = 100;
    const pickActive = () => {
      const sections = Array.from(document.querySelectorAll("[data-nav-signal]"));
      const vh = window.innerHeight || document.documentElement.clientHeight;

      let best = null;
      let bestScore = Infinity;
      let bestPriority = -Infinity;

      for (const el of sections) {
        const rect = el.getBoundingClientRect();
        const visible = rect.bottom > NAV_HEIGHT && rect.top < vh;
        if (!visible) continue;

        const priority = Number(el.dataset.navPriority || 0);
        const score = Math.abs(rect.top - NAV_HEIGHT);

        if (
          priority > bestPriority ||
          (priority === bestPriority && score < bestScore)
        ) {
          best = el;
          bestScore = score;
          bestPriority = priority;
        }
      }

      if (best) {
        setState({
          useOverride: String(best.dataset.navOverride).toLowerCase() === "true",
          hideBrand: String(best.dataset.hideBrand).toLowerCase() === "true",
        });
      }
    };

    const io = new IntersectionObserver(pickActive, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    const sections = document.querySelectorAll("[data-nav-signal]");
    sections.forEach((el) => io.observe(el));

    pickActive();
    window.addEventListener("scroll", pickActive, { passive: true });
    window.addEventListener("resize", pickActive);

    const mo = new MutationObserver(() => {
      io.disconnect();
      document.querySelectorAll("[data-nav-signal]").forEach((el) => io.observe(el));
      pickActive();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", pickActive);
      window.removeEventListener("resize", pickActive);
    };
  }, []);

  // 动态颜色和品牌图
  const textColor = state.useOverride ? "#000" : "#fff";
  const brandImgSrc = state.useOverride ? "/brand/dark.png" : "/brand/white.png";

  const styles = {
    header: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "100px",
      display: "flex",
      alignItems: "center",
      background: "transparent",
      zIndex: 10000,
      borderBottom: "1px solid rgba(0,0,0,.06)"
    },
    inner: {
      width: "min(1200px,92vw)",
      margin: "0 auto",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    brandContainer: {
      display: "flex",
      alignItems: "center",
      gap: "8px"
    },
    brandText: {
      fontWeight: 700,
      color: textColor,
      fontFamily:'Georgia, "Times New Roman", serif'
    },
    brandIcon: {
      width: "108px",
      height: "108px",
      objectFit: "contain"
    },
    links: {
      display: "flex",
      gap: "16px",
      alignItems: "center"
    },
    link: {
      display: "block",
      padding: "8px 10px",
      borderRadius: "8px",
      textDecoration: "none",
      color: textColor,
      lineHeight: 1.1
    },
    linkSpan: {
      fontSize: "11px",
      opacity: 0.7
    },
    activeLink: {
      background: "rgba(0,0,0,.08)"
    },
    langBtn: {
      marginLeft: "12px",
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      background: "transparent",
      cursor: "pointer",
      fontSize: "12px"
    },
    hamburger: {
      fontSize: "28px",
      cursor: "pointer",
      color: textColor,
      background: "none",
      border: "none"
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 20000,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    modalContent: {
      background: "#fff",
      padding: "2rem",
      borderRadius: "12px",
      width: "90%",
      maxWidth: "400px",
      textAlign: "center"
    }
  };

  // 桌面端的链接样式（受 override 影响）
  const navLinkStyleDesktop = ({ isActive }) => ({
    ...styles.link,
    ...(isActive ? styles.activeLink : {})
  });

  // 移动端的链接样式（固定，不受 override 影响）
  const navLinkStyleMobile = ({ isActive }) => ({
    textDecoration: "none",
    color: "#000",
    fontSize: "16px",
    lineHeight: 1.4,
    ...(isActive ? { fontWeight: "bold" } : {})
  });

  // 语言感知
  const location = useLocation();
  const navigate = useNavigate();
  const isEnglish = location.pathname.startsWith("/en");
  const prefix = isEnglish ? "/en" : ""; 

  const switchLang = () => {
    if (isEnglish) {
      navigate(location.pathname.replace(/^\/en/, "") || "/");
    } else {
      navigate("/en" + location.pathname);
    }
  };

  return (
    <header style={styles.header}>
      <div style={styles.inner}>
        <div
          style={{
            ...styles.brandContainer,
            visibility: state.hideBrand ? "hidden" : "visible"
          }}
        >
          <img src={brandImgSrc} alt="Brand Icon" style={styles.brandIcon} />
          <div style={styles.brandText}>重新讲述 · 档案展</div>
        </div>

        {/* 桌面端：原始导航 */}
        {!isMobile && (
          <nav style={styles.links}>
            <NavLink to={`${prefix}/Aesthetics`} style={navLinkStyleDesktop}>
              重述美学<br/><span style={styles.linkSpan}>Aesthetics</span>
            </NavLink>
            <NavLink to={`${prefix}/Tradition`} style={navLinkStyleDesktop}>
              重述传统<br/><span style={styles.linkSpan}>Tradition</span>
            </NavLink>
            <NavLink to={`${prefix}/Innovation`} style={navLinkStyleDesktop}>
              重述革新<br/><span style={styles.linkSpan}>Innovation</span>
            </NavLink>
            <NavLink to={`${prefix}/Craft`} style={navLinkStyleDesktop}>
              重述匠心<br/><span style={styles.linkSpan}>Craft</span>
            </NavLink>

            <button onClick={switchLang} style={{ ...styles.langBtn, color: textColor }}>
              {isEnglish ? "中文" : "EN"}
            </button>
          </nav>
        )}

        {/* 移动端：汉堡按钮 */}
        {isMobile && (
          <button
            style={styles.hamburger}
            onClick={() => setMenuOpen(true)}
          >
            ☰
          </button>
        )}
      </div>

      {/* 移动端：拟态框菜单（固定样式，不受 override 影响） */}
      {isMobile && menuOpen && (
        <div style={styles.modalOverlay} onClick={() => setMenuOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <nav style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <NavLink to={`${prefix}/Aesthetics`} style={navLinkStyleMobile} onClick={() => setMenuOpen(false)}>
                重述美学 · Aesthetics
              </NavLink>
              <NavLink to={`${prefix}/Tradition`} style={navLinkStyleMobile} onClick={() => setMenuOpen(false)}>
                重述传统 · Tradition
              </NavLink>
              <NavLink to={`${prefix}/Innovation`} style={navLinkStyleMobile} onClick={() => setMenuOpen(false)}>
                重述革新 · Innovation
              </NavLink>
              <NavLink to={`${prefix}/Craft`} style={navLinkStyleMobile} onClick={() => setMenuOpen(false)}>
                重述匠心 · Craft
              </NavLink>

              <button onClick={switchLang} style={{ ...styles.langBtn, color: "#000", marginTop: "16px" }}>
                {isEnglish ? "中文" : "EN"}
              </button>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
